import { create } from "zustand";

type LangText = {
  en?: string;
  id?: string;
};

type SimpleEntity = {
  id: number;
  name: LangText;
};

type LocationEntity = {
  id: number;
  name: string;
};

type Skill = {
  id: number;
  name: string;
};

type Allowance = {
  id: number;
  name: LangText;
  amount: number | null;
};

type Language = {
  id: number;
  name: LangText;
  proficiency: {
    id: number;
    name: LangText;
  };
};

type WorkExperience = {
  id: number;
  candidate_id: number;

  industry_id: number;
  industry_name: LangText;

  job_type_id: number;
  job_type_name: LangText;

  name: string; // nama perusahaan
  job_title: string;

  start_date: string; // ISO string
  end_date: string | null; // bisa null
  is_working_here: boolean;

  responsibility: string;
  achievement: string;

  created_at: string;
  updated_at: string;

  industry: {
    id: number;
    name: LangText;
  };

  job_type: {
    id: number;
    name: LangText;
  };
};

type Education = {
  id: number;
  candidate_id: number;

  degree_id: number;
  degree_name: LangText;

  school_name: string;
  major: string;

  start_date: string; // ISO string
  end_date: string | null;
  is_currently_study: boolean;

  created_at: string;
  updated_at: string;

  degree: {
    id: number;
    name: LangText;
  };
};

type Certification = {
  id: number;
  candidate_id: number;
  title: string;
  issuer: string;
  issue_date: string;
  expire_date: string | null;
  is_lifetime: boolean;
  image: string | null;
  created_at: string;
  updated_at: string;
};


export type CandidateProfile = {
  id: number;
  user_id: number;
  name: string;
  email: string;
  phone: string;
  photo: string | null;
  birth_date: string;
  city_name: string;
  state_name: string;
  country_name: string;
  gender_name: any;
  martial_status_name: any;
  experience_year_name: any;
  experience_level_name: any;
  degree_name: any;
  country_id: number;
  state_id: number;
  city_id: number;
  gender_id: number;
  martial_status_id: number;
  join_availability_id: number;

  about: any;
  experience_year_id: number;
  experience_level_id: number;
  degree_id: number;

  current_salary: number;
  expected_salary: number;
  current_salary_method: "gross" | "nett";
  expected_salary_method: "gross" | "nett";
  is_negotiable: boolean;

  website: string;
  available_for_hiring: boolean;
  status: boolean;
  resume: string;

  created_at: string;
  updated_at: string;

  gender: SimpleEntity;
  martial_status: SimpleEntity;
  join_availability: SimpleEntity;
  experience_year: SimpleEntity;
  experience_level: SimpleEntity;
  degree: SimpleEntity;

  country: LocationEntity;
  state: LocationEntity;
  city: LocationEntity;

  skills: Skill[];
  job_preferences: SimpleEntity[];
  allowances: Allowance[];
  languages: Language[];
  work_experiences: WorkExperience[];
  educations: Education[];
  certifications: Certification[];
};


type CandidateProfileStore = {
  profile: CandidateProfile | null;
  loading: boolean;
  fetchCandidateProfile: () => Promise<void>;
};

/* ================= TOKEN ================= */
const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.accessToken || null;
  } catch {
    return null;
  }
};

const REQUIRED_PROFILE_FIELDS = [
  "name",
  "phone",
  "birth_date",
  "country_id",
  "state_id",
  "city_id",
  "gender_id",
  "martial_status_id",
  "join_availability_id",
  "experience_year_id",
  "experience_level_id",
  "degree_id",
  "current_salary",
  "expected_salary",
  "resume",
];

const REQUIRED_ARRAY_FIELDS = [
  "skills",
  "languages",
  "job_preferences",
  "educations",
  // "allowances",
  // "work_experiences",
  // "certifications",
  // "projects"
];

const isProfileCompleted = (profile: any): boolean => {
  if (!profile) return false;

  // cek field scalar (null / empty)
  const scalarInvalid = REQUIRED_PROFILE_FIELDS.some(
    (key) => profile[key] === null || profile[key] === ""
  );

  if (scalarInvalid) return false;

  // cek array wajib
  const arrayInvalid = REQUIRED_ARRAY_FIELDS.some(
    (key) => !Array.isArray(profile[key]) || profile[key].length === 0
  );

  return !arrayInvalid;
};

const updateAuthStorageDataProfile = (value: boolean) => {
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return;

    const parsed = JSON.parse(raw);

    localStorage.setItem(
      "auth-storage",
      JSON.stringify({
        ...parsed,
        state: {
          ...parsed.state,
          dataProfile: value,
        },
      })
    );
  } catch (e) {
    console.error("Failed to update auth-storage", e);
  }
};



/* ================= STORE ================= */
export const useCandidateProfileStore = create<CandidateProfileStore>((set) => ({
  profile: null,
  loading: false,

  fetchCandidateProfile: async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      set({ loading: true });

      const res = await fetch(
        "https://admin-talentlink.digiline.id/api/candidate/profile",
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 401) {
        localStorage.removeItem("auth-storage");
        window.location.href = "/";
        return;
      }

      if (!res.ok) {
        throw new Error("Failed to fetch candidate profile");
      }

      const json = await res.json();
      const profile = json.data ?? null;

      // ✅ cek profile lengkap
      const completed = isProfileCompleted(profile);

      // ✅ update localStorage auth-storage
      updateAuthStorageDataProfile(completed);

      set({ profile });
    } catch (e) {
      console.error("❌ Fetch candidate profile failed:", e);
    } finally {
      set({ loading: false });
    }
  },

}));
